import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Eye, 
  Search,
  Calendar,
  Filter,
  CheckCircle,
  Clock,
  XCircle,
  Plus
} from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./ui/accordion";

const AdminCandidatiList = ({ candidati, onImpersonate, loadingImpersonation }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedCandidato, setSelectedCandidato] = useState(null);
  const [showScheda, setShowScheda] = useState(false);

  // Filtra candidati in base a ricerca e filtro stato
  const normalizedCandidati = Array.isArray(candidati) ? candidati : [];
  const lowerCaseSearchTerm = searchTerm.toLowerCase();

  // Ordina per id decrescente
  const sortedCandidati = [...normalizedCandidati].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));

  const filteredCandidati = sortedCandidati.filter((candidato) => {
    // Usa nome e cognome reali per la ricerca
    const nomeCompleto = `${candidato?.nome || ""} ${candidato?.cognome || ""}`.toLowerCase().trim();
    const email = (candidato?.email ?? "").toLowerCase();
    const stato = candidato?.stato_pipeline || candidato?.stato;

    const matchesSearch =
      nomeCompleto.includes(lowerCaseSearchTerm) || email.includes(lowerCaseSearchTerm);

    const matchesStatus = statusFilter === "all" || stato === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (stato) => {
    switch (stato) {
      case 'nuovo': return <Clock className="w-4 h-4 text-blue-600" />;
      case 'contattato': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'qualificato': return <CheckCircle className="w-4 h-4 text-purple-600" />;
      case 'rifiutato': return <XCircle className="w-4 h-4 text-red-600" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (stato) => {
    switch (stato) {
      case 'nuovo': return 'bg-blue-100 text-blue-800';
      case 'contattato': return 'bg-green-100 text-green-800';
      case 'qualificato': return 'bg-purple-100 text-purple-800';
      case 'rifiutato': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card className="border-0 rounded-2xl">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-3">
            <User className="w-6 h-6 text-blue-600" />
            Gestione Candidati
            <Badge variant="outline" className="ml-2">
              {filteredCandidati.length} di {normalizedCandidati.length}
            </Badge>
          </CardTitle>
          
          {/* Pulsanti Azione */}
          <div className="flex gap-2">
            <Button
              onClick={() => navigate('/candidato-onboarding')}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Candidato
            </Button>
            <Button
              onClick={() => {
                // Funzionalità Invia Modulo (da implementare)
                alert('Funzionalità Invia Modulo in arrivo');
              }}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <Mail className="w-4 h-4 mr-2" />
              Invia Modulo
            </Button>
          </div>
        </div>
        
        {/* Filtri e Ricerca */}
        <div className="flex gap-4 mt-4">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Cerca per nome o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-xl border-gray-200"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
          >
            <option value="all">Tutti gli stati</option>
            <option value="nuovo">Nuovo</option>
            <option value="contattato">Contattato</option>
            <option value="qualificato">Qualificato</option>
            <option value="rifiutato">Rifiutato</option>
          </select>
        </div>
      </CardHeader>
      <CardContent>
        {filteredCandidati.length === 0 ? (
          <div className="text-center py-8">
            <User className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {normalizedCandidati.length === 0 ? "Nessun candidato presente" : "Nessun candidato trovato"}
            </p>
          </div>
        ) : (
          <Accordion type="single" collapsible className="space-y-3">
            {filteredCandidati.map((candidato) => (
              <AccordionItem key={candidato.id} value={String(candidato.id)} className="bg-gray-50 rounded-xl p-4 transition-colors">
                <AccordionTrigger className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    {/* Avatar */}
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    {/* Info Candidato */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {candidato?.nome && candidato?.cognome
                            ? `${candidato.nome} ${candidato.cognome}${candidato.zona_preferenza_lavorativa ? ` - ${candidato.zona_preferenza_lavorativa}` : ""}`
                            : (candidato?.nome || candidato?.cognome || "Nome non disponibile")}
                        </h3>
                        <Badge className={getStatusColor(candidato.stato)}>
                          {getStatusIcon(candidato.stato)}
                          <span className="ml-1 capitalize">{candidato.stato}</span>
                        </Badge>
                        {candidato.esperienza_immobiliare && (
                          <Badge variant="outline" className="text-green-600 border-green-300">
                            Esperienza
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-2 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <span className="truncate">{candidato.email || "Email non disponibile"}</span>
                        </div>
                        {candidato.telefono && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            <span>{candidato.telefono}</span>
                          </div>
                        )}
                        {candidato.citta && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span>{candidato.citta}</span>
                          </div>
                        )}
                      </div>
                      {candidato.data_registrazione && (
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                          <Calendar className="w-3 h-3" />
                          <span>
                            Registrato: {new Date(candidato.data_registrazione).toLocaleDateString('it-IT')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Azioni */}
                  <div className="flex items-center gap-2 mt-3 sm:mt-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const nome = candidato?.nome?.trim();
                        const cognome = candidato?.cognome?.trim();
                        const nomeCompleto = nome && cognome ? `${nome} ${cognome}` : (nome || cognome || "SantoloToscano");
                        onImpersonate('candidato', candidato.id, nomeCompleto);
                      }}
                      className="w-full sm:w-auto rounded-xl border-blue-300 text-blue-600 hover:bg-blue-50 text-xs px-3 py-2"
                    >
                      <Eye className="w-4 h-4 mr-1" />Visualizza Dashboard
                    </Button>
                  </div>
                  {/* Freccetta + testo Scheda Candidato */}
                  <span className="flex items-center gap-2 text-sm font-semibold text-blue-700 ml-4">
                    Scheda Candidato
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="p-2 max-w-lg">
                    <h2 className="text-xl font-bold mb-2">Scheda Candidato</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div><strong>Nome e Cognome:</strong> {candidato.nome} {candidato.cognome}</div>
                        <div><strong>Età:</strong> {candidato.eta || '-'}</div>
                        <div><strong>Città:</strong> {candidato.citta || '-'}</div>
                        <div><strong>Esperienza:</strong> {candidato.esperienza_lavorativa || '-'}</div>
                        <div><strong>Studi:</strong> {candidato.titolo_studio || '-'}</div>
                      </div>
                      <div className="space-y-2">
                        <div><strong>Curriculum allegato:</strong> {candidato.cv_url ? (<a href={candidato.cv_url} target="_blank" rel="noopener noreferrer">Scarica CV</a>) : '-'}</div>
                        <div><strong>Video candidatura:</strong> {candidato.video_url ? (<a href={candidato.video_url} target="_blank" rel="noopener noreferrer">Guarda Video</a>) : '-'}</div>
                        <div><strong>Note:</strong> {candidato.note_pubbliche || '-'}</div>
                        <div><strong>Storico colloqui:</strong> {candidato.storico_colloqui ? candidato.storico_colloqui.join(', ') : '-'}</div>
                        <div><strong>Agenzie compatibili:</strong> {candidato.agenzie_compatibili ? candidato.agenzie_compatibili.join(', ') : '-'}</div>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminCandidatiList;

// Animazioni modale (aggiungi in un file CSS globale, es: index.css o App.css)
/*
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.2s ease-out;
}

.animate-scaleIn {
  animation: scaleIn 0.2s ease-out;
}
*/